import {
  TotalSupplyAbi,
  YieldBasisAmmReadAbi,
  YieldBasisLtReadAbi,
} from '../abis'
import type { SavingsRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type SavingsReader } from './shared'

/** `preview_withdraw` is quoted per **one whole share**; LT shares are
 *  always 18-decimal (the contract hardcodes `decimals = 18`) regardless
 *  of what the underlying uses. */
const ONE_SHARE = ONE_E18

/**
 * Yield Basis `yb-<ASSET>` — five calls across two contracts:
 *   1. `LT.totalSupply()`
 *   2. `LT.pricePerShare()`          — fundamental, 1e18 in ASSET units
 *   3. `LT.preview_withdraw(1e18)`   — redeemable, RAW underlying units
 *   4. `AMM.max_debt()`              — capacity numerator
 *   5. `AMM.value_oracle()`          — `[lpPrice, equityInCrvUsd]`
 *
 * Each market is a **2×-leveraged Curve Cryptoswap LP with impermanent
 * loss cancelled**, so the share tracks BTC/ETH 1:1 and trading fees are
 * the yield. It is not a yield-accrual vault: when rebalance slippage
 * outruns fees the share price *falls* in asset terms, which is why the
 * four entries are flagged in `VOLATILE_VAULT_OVERRIDES`.
 *
 * ## Why not the generic 4626 reader
 *
 * `LT` implements `IERC20` and nothing else of ERC-4626 — no `asset()`,
 * `totalAssets()`, `convertToAssets()` or `redeem()`. The strict-4626
 * surface in this protocol lives on the **gauge**, which is deliberately
 * not registered: a staked position forfeits PPS growth entirely and
 * earns YB emissions instead, so the gauge's `convertToAssets` *declines*
 * over time. Registering it would publish a permanently negative APR for
 * a product that is actually paying 3–6 %.
 *
 * ## Two per-share values, and why `exchangeRate` is the redeemable one
 *
 * `pricePerShare()` is oracle-anchored and sandwich-resistant;
 * `preview_withdraw()` runs a live Cryptoswap unwind. Their signed gap is
 * the protocol's **TRD** (Temporary Redemption Discount) — usually a few
 * basis points, negative during a drawdown, resolving via arbitrage
 * within hours. `exchangeRate` takes the *redeemable* figure so the
 * vault is never marked above what a holder can actually get (the
 * `previewRedeem`-over-`convertToAssets` choice we already make for
 * Strata); the fundamental value is carried alongside so a consumer can
 * render the discount rather than wonder why we disagree with YB's UI.
 *
 * ## Scaling
 *
 * `pricePerShare` answers 1e18-scaled in **asset** units whatever the
 * asset's decimals, while `preview_withdraw` answers in **raw
 * underlying**. With 8-decimal cbBTC/WBTC the two differ by 1e10, so
 * they are normalised in opposite directions here. `totalAssets` is
 * `totalSupply × preview_withdraw(1e18) / 1e18` — shares are 18-decimal,
 * so this lands in raw underlying with no further rescaling.
 *
 * ## Deposit capacity
 *
 * There is no `maxDeposit()`. `LT.deposit` asserts
 * `AMM.max_debt() / 2 >= value`, reverting `"Debt too high"`, so the free
 * room is `max_debt()/2 − value` **in crvUSD**. Rather than fetch an
 * asset price to convert it, we scale by the ratio the two share:
 *
 *   depositCapacity = totalAssets × (max_debt/2 − value) / value
 *
 * which is exact (both legs are the same equity measured in different
 * units) and price-free. A market at or over its cap yields `0n`, which
 * is the honest answer — three of the four markets sit above 95 % full
 * and WETH is over cap and reverting.
 */
export const readerYieldBasisLt = (
  entry: SavingsRegistryEntry,
): SavingsReader => {
  const underlyingUnit =
    10n ** BigInt(entry.underlyingDecimals ?? entry.decimals)
  // Guaranteed by the registry entry's own doc; the reader cannot read a
  // market's cap without it, so fall back to the share token, whose
  // `max_debt`/`value_oracle` calls then fail and drop the entry rather
  // than silently reporting infinite capacity.
  const amm = entry.capacityContract ?? entry.address
  return {
    calls: [
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: entry.address, name: 'pricePerShare', params: [] },
      { address: entry.address, name: 'preview_withdraw', params: [ONE_SHARE] },
      { address: amm, name: 'max_debt', params: [] },
      { address: amm, name: 'value_oracle', params: [] },
    ],
    abis: [
      TotalSupplyAbi,
      YieldBasisLtReadAbi,
      YieldBasisLtReadAbi,
      YieldBasisAmmReadAbi,
      YieldBasisAmmReadAbi,
    ],
    parse: ([supply, pps, redeemPerShare, maxDebt, valueOracle]) => {
      const totalSupply = toBigInt(supply)
      const fundamental = toBigInt(pps)
      const redeemRaw = toBigInt(redeemPerShare)
      if (
        totalSupply === undefined ||
        fundamental === undefined ||
        redeemRaw === undefined
      ) {
        return undefined
      }
      // An empty market answers `pricePerShare() == 1e18` by
      // short-circuit while `preview_withdraw` divides by a zero supply
      // and reverts — so this only guards a market drained to exactly
      // zero between the two calls.
      if (totalSupply === 0n || redeemRaw === 0n) return undefined

      const totalAssets = (totalSupply * redeemRaw) / ONE_SHARE
      const exchangeRate = (redeemRaw * ONE_E18) / underlyingUnit

      // `value_oracle` is declared as two flat outputs, but a decoder
      // that keeps the Vyper struct shape would hand back an object —
      // accept both rather than lose capacity over a decoder detail.
      const equity = Array.isArray(valueOracle)
        ? toBigInt(valueOracle[1])
        : toBigInt((valueOracle as { value?: unknown } | undefined)?.value)
      const cap = toBigInt(maxDebt)
      let depositCapacity: bigint | undefined
      if (cap !== undefined && equity !== undefined && equity > 0n) {
        const headroom = cap / 2n - equity
        depositCapacity = headroom > 0n ? (totalAssets * headroom) / equity : 0n
      }

      return {
        totalAssets,
        totalSupply,
        exchangeRate,
        fundamentalExchangeRate: fundamental,
        depositCapacity,
      }
    },
  }
}
